import { useState, useRef, useCallback } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { X, Check, RotateCw } from 'lucide-react';

const ASPECT_RATIOS = [
  { label: 'Free', value: undefined },
  { label: '1:1', value: 1 },
  { label: '4:3', value: 4 / 3 },
  { label: '3:2', value: 3 / 2 },
  { label: '16:9', value: 16 / 9 },
  { label: '9:16', value: 9 / 16 },
  { label: '3:4', value: 3 / 4 },
];

function makeCenteredCrop(aspect, imgWidth, imgHeight) {
  if (!aspect) {
    return centerCrop({ unit: '%', width: 80, height: 80 }, imgWidth, imgHeight);
  }
  return centerCrop(
    makeAspectCrop({ unit: '%', width: 80 }, aspect, imgWidth, imgHeight),
    imgWidth, imgHeight
  );
}

export default function CropModal({ imageSrc, onConfirm, onCancel }) {
  const imgRef = useRef(null);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [aspectRatio, setAspectRatio] = useState(undefined); // Free by default
  const [rotation, setRotation] = useState(0);
  const canvasRef = useRef(null);

  const onImageLoad = useCallback((e) => {
    const { naturalWidth, naturalHeight } = e.currentTarget;
    imgRef.current = e.currentTarget;
    const newCrop = makeCenteredCrop(aspectRatio, naturalWidth, naturalHeight);
    setCrop(newCrop);
  }, [aspectRatio]);

  const handleAspectChange = useCallback((ratio) => {
    setAspectRatio(ratio);
    if (imgRef.current) {
      const { naturalWidth, naturalHeight } = imgRef.current;
      const newCrop = makeCenteredCrop(ratio, naturalWidth, naturalHeight);
      setCrop(newCrop);
      setCompletedCrop(null);
    }
  }, []);

  const handleRotate = useCallback(() => {
    setRotation(r => (r + 90) % 360);
  }, []);

  const handleConfirm = useCallback(() => {
    if (!imgRef.current || !completedCrop) return;

    const canvas = document.createElement('canvas');
    const img = imgRef.current;
    const scaleX = img.naturalWidth / img.width;
    const scaleY = img.naturalHeight / img.height;

    const cropX = completedCrop.x * scaleX;
    const cropY = completedCrop.y * scaleY;
    const cropW = completedCrop.width * scaleX;
    const cropH = completedCrop.height * scaleY;

    // Handle rotation
    const isRotated = rotation === 90 || rotation === 270;
    canvas.width = isRotated ? cropH : cropW;
    canvas.height = isRotated ? cropW : cropH;

    const ctx = canvas.getContext('2d');
    ctx.save();
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.drawImage(img, cropX, cropY, cropW, cropH, -cropW / 2, -cropH / 2, cropW, cropH);
    ctx.restore();

    const croppedDataUrl = canvas.toDataURL('image/png');
    onConfirm(croppedDataUrl, { width: canvas.width, height: canvas.height });
  }, [completedCrop, onConfirm, rotation]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" data-testid="crop-modal">
      <div className="bg-[#111111] border border-white/10 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div>
            <h3 className="font-['Outfit'] text-base font-bold text-white">Crop Your Image</h3>
            <p className="text-xs text-slate-500 mt-0.5">Drag to adjust the crop area</p>
          </div>
          <button onClick={onCancel} data-testid="crop-cancel-x" className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-white hover:bg-white/10 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Aspect Ratio Presets */}
        <div className="flex items-center gap-2 px-5 py-3 border-b border-white/5 overflow-x-auto scrollbar-hide">
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mr-1 whitespace-nowrap">Ratio</span>
          {ASPECT_RATIOS.map(({ label, value }) => (
            <button
              key={label}
              onClick={() => handleAspectChange(value)}
              data-testid={`crop-ratio-${label.replace(/[:/]/g, '-')}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                aspectRatio === value
                  ? 'bg-[#00E5FF] text-[#0A0A0A]'
                  : 'bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white'
              }`}
            >
              {label}
            </button>
          ))}
          <div className="w-px h-5 bg-white/10 mx-1 flex-shrink-0" />
          <button
            onClick={handleRotate}
            data-testid="crop-rotate-btn"
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white transition-all whitespace-nowrap"
          >
            <RotateCw className="w-3 h-3" />
            Rotate
          </button>
        </div>

        {/* Crop Area */}
        <div className="flex-1 overflow-auto p-5 flex items-center justify-center bg-[#0A0A0A]">
          <ReactCrop
            crop={crop}
            onChange={(c) => setCrop(c)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={aspectRatio}
            keepSelection
            className="max-w-full"
          >
            <img
              src={imageSrc}
              alt="Upload to crop"
              onLoad={onImageLoad}
              className="max-h-[55vh] max-w-full"
              style={{ transform: `rotate(${rotation}deg)`, transition: 'transform 0.3s ease' }}
              data-testid="crop-image"
            />
          </ReactCrop>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-white/10">
          <p className="text-[10px] text-slate-500">
            {aspectRatio ? `${ASPECT_RATIOS.find(r => r.value === aspectRatio)?.label || ''} ratio` : 'Free crop'}
            {rotation > 0 && ` · ${rotation}°`}
          </p>
          <div className="flex gap-2">
            <button onClick={onCancel} data-testid="crop-cancel-btn" className="px-4 py-2.5 rounded-xl text-xs font-medium text-slate-400 border border-white/10 hover:text-white hover:border-white/20 transition-colors">
              Cancel
            </button>
            <button onClick={handleConfirm} disabled={!completedCrop} data-testid="crop-confirm-btn"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-xs font-semibold bg-[#00E5FF] text-[#0A0A0A] hover:bg-white transition-colors disabled:opacity-50">
              <Check className="w-3.5 h-3.5" /> Apply Crop
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
